import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, AlertTriangle, Users, Building2, FileText, UserCheck, Percent, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { ClientGroupMigrationPreviewResponse, ClientGroupMigrationExecuteResponse } from "@shared/schema";

interface ConflictResolutionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  previewData: ClientGroupMigrationPreviewResponse;
  onResolved: (result: ClientGroupMigrationExecuteResponse) => void;
  onCancel: () => void;
}

export function ConflictResolutionModal({
  open,
  onOpenChange,
  previewData,
  onResolved,
  onCancel,
}: ConflictResolutionModalProps) {
  const { toast } = useToast();
  const [selectedMasterGroupId, setSelectedMasterGroupId] = useState<string>("");

  const executeMigrationMutation = useMutation({
    mutationFn: async () => {
      const sourceGroupIds = previewData.clientGroups
        .filter(g => g.clientGroupId !== selectedMasterGroupId)
        .map(g => g.clientGroupId);
      
      const response = await apiRequest("POST", "/api/crm/client-group-migrations/execute", {
        masterClientGroupId: selectedMasterGroupId,
        sourceClientGroupIds: sourceGroupIds,
      });
      return response.json() as Promise<ClientGroupMigrationExecuteResponse>;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/client-groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals"] });
      
      toast({
        title: "Migration Complete",
        description: `Successfully migrated ${result.summary.contactsMigrated} contacts, ${result.summary.accountsMigrated} accounts, and ${result.summary.dealsMigrated} deals to "${result.masterClientGroupName}"`,
      });
      
      onResolved(result);
    },
    onError: (error: Error) => {
      toast({
        title: "Migration Failed",
        description: error.message || "Failed to migrate client groups",
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    setSelectedMasterGroupId("");
    onOpenChange(false);
    onCancel();
  };

  const handleConfirm = () => {
    if (!selectedMasterGroupId) {
      toast({
        title: "Selection Required",
        description: "Please select a master client group",
        variant: "destructive",
      });
      return;
    }
    executeMigrationMutation.mutate();
  };

  const getApplicantRoleLabel = (role: string) => {
    switch (role) {
      case "primary_applicant":
        return "Primary";
      case "co_applicant":
        return "Co-Applicant";
      case "guarantor":
        return "Guarantor";
      default:
        return role;
    }
  };

  const getApplicantTypeIcon = (type: string) => {
    return type === "contact" ? (
      <Users className="h-3 w-3 text-muted-foreground" />
    ) : (
      <Building2 className="h-3 w-3 text-muted-foreground" />
    );
  };

  const calculateMigrationImpact = () => {
    if (!selectedMasterGroupId) return null;
    
    const sourceGroups = previewData.clientGroups.filter(g => g.clientGroupId !== selectedMasterGroupId);
    
    const totals = {
      contacts: 0,
      accounts: 0,
      deals: 0,
      directors: 0,
      shareholders: 0,
      trustees: 0,
    };
    
    sourceGroups.forEach(group => {
      totals.contacts += group.affectedCounts.contacts;
      totals.accounts += group.affectedCounts.accounts;
      totals.deals += group.affectedCounts.deals;
      totals.directors += group.affectedCounts.directors;
      totals.shareholders += group.affectedCounts.shareholders;
      totals.trustees += group.affectedCounts.trustees;
    });
    
    return totals;
  };

  const migrationImpact = calculateMigrationImpact();
  const masterGroup = previewData.clientGroups.find(g => g.clientGroupId === selectedMasterGroupId);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-warning" />
            Client Group Conflict Detected
          </DialogTitle>
          <DialogDescription className="text-xs">
            The applicants you selected belong to different client groups. Select which group should be the master,
            and all entities will be migrated to that group.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <RadioGroup
            value={selectedMasterGroupId}
            onValueChange={setSelectedMasterGroupId}
            className="space-y-3"
          >
            {previewData.clientGroups.map((group) => (
              <div
                key={group.clientGroupId}
                className={`border rounded p-3 transition-colors ${
                  selectedMasterGroupId === group.clientGroupId
                    ? "border-primary bg-primary/5"
                    : "border-border hover-elevate"
                }`}
              >
                <div className="flex items-start gap-3">
                  <RadioGroupItem
                    value={group.clientGroupId}
                    id={group.clientGroupId}
                    className="mt-0.5"
                    data-testid={`radio-client-group-${group.clientGroupId}`}
                  />
                  <div className="flex-1 space-y-2">
                    <Label
                      htmlFor={group.clientGroupId}
                      className="font-medium text-sm cursor-pointer"
                    >
                      {group.clientGroupName}
                      {selectedMasterGroupId === group.clientGroupId && (
                        <Badge variant="default" className="ml-2 text-xs">
                          Master
                        </Badge>
                      )}
                    </Label>

                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium">Applicants in this deal:</span>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {group.applicants.map((applicant) => (
                          <div
                            key={`${applicant.applicantType}-${applicant.applicantId}`}
                            className="inline-flex items-center gap-1 bg-muted px-2 py-0.5 rounded text-xs"
                          >
                            {getApplicantTypeIcon(applicant.applicantType)}
                            <span>{applicant.name}</span>
                            <Badge variant="outline" className="text-[10px] px-1 py-0">
                              {getApplicantRoleLabel(applicant.applicantRole)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-xs pt-1 border-t mt-2">
                      <div className="flex items-center gap-1.5" data-testid={`count-contacts-${group.clientGroupId}`}>
                        <Users className="h-3 w-3 text-muted-foreground" />
                        <span>{group.affectedCounts.contacts} contacts</span>
                      </div>
                      <div className="flex items-center gap-1.5" data-testid={`count-accounts-${group.clientGroupId}`}>
                        <Building2 className="h-3 w-3 text-muted-foreground" />
                        <span>{group.affectedCounts.accounts} accounts</span>
                      </div>
                      <div className="flex items-center gap-1.5" data-testid={`count-deals-${group.clientGroupId}`}>
                        <FileText className="h-3 w-3 text-muted-foreground" />
                        <span>{group.affectedCounts.deals} deals</span>
                      </div>
                      <div className="flex items-center gap-1.5" data-testid={`count-directors-${group.clientGroupId}`}>
                        <UserCheck className="h-3 w-3 text-muted-foreground" />
                        <span>{group.affectedCounts.directors} directors</span>
                      </div>
                      <div className="flex items-center gap-1.5" data-testid={`count-shareholders-${group.clientGroupId}`}>
                        <Percent className="h-3 w-3 text-muted-foreground" />
                        <span>{group.affectedCounts.shareholders} shareholders</span>
                      </div>
                      <div className="flex items-center gap-1.5" data-testid={`count-trustees-${group.clientGroupId}`}>
                        <Scale className="h-3 w-3 text-muted-foreground" />
                        <span>{group.affectedCounts.trustees} trustees</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </RadioGroup>

          {migrationImpact && masterGroup && (
            <div className="bg-warning/10 border border-warning/30 rounded p-3 text-xs">
              <div className="font-medium text-warning mb-1">Migration Summary</div>
              <p className="text-muted-foreground mb-2">
                The following will be migrated to "{masterGroup.clientGroupName}":
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div data-testid="migration-summary-contacts">
                  <span className="font-medium">{migrationImpact.contacts}</span> contacts
                </div>
                <div data-testid="migration-summary-accounts">
                  <span className="font-medium">{migrationImpact.accounts}</span> accounts
                </div>
                <div data-testid="migration-summary-deals">
                  <span className="font-medium">{migrationImpact.deals}</span> deals
                </div>
                <div data-testid="migration-summary-directors">
                  <span className="font-medium">{migrationImpact.directors}</span> directors
                </div>
                <div data-testid="migration-summary-shareholders">
                  <span className="font-medium">{migrationImpact.shareholders}</span> shareholders
                </div>
                <div data-testid="migration-summary-trustees">
                  <span className="font-medium">{migrationImpact.trustees}</span> trustees
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={executeMigrationMutation.isPending}
            data-testid="button-cancel-migration"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedMasterGroupId || executeMigrationMutation.isPending}
            data-testid="button-confirm-migration"
          >
            {executeMigrationMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Migrating...
              </>
            ) : (
              "Confirm Migration"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
